/**
 * Relative Date Operators for MUI DataGrid
 *
 * Creates custom date operators that support relative date filtering
 * (e.g., "2 weeks ago", "1 month in the future").
 * Based on the DateCondition component from the expression builder.
 */

import React from 'react'
import { getGridDateOperators } from '@mui/x-data-grid-premium'

/**
 * Relative date options matching DateCondition.jsx format
 */
const relativeOptions = [
  { value: '-w', positive: false, unit: 'w', label: 'weken in het verleden' },
  { value: '+w', positive: true, unit: 'w', label: 'weken in de toekomst' },
  { value: '-W', positive: false, unit: 'W', label: 'weken in het verleden - op maandag' },
  { value: '+W', positive: true, unit: 'W', label: 'weken in de toekomst - op maandag' },
  { value: '-m', positive: false, unit: 'm', label: 'maanden in het verleden' },
  { value: '+m', positive: true, unit: 'm', label: 'maanden in de toekomst' },
  { value: '-M', positive: false, unit: 'M', label: 'maanden in het verleden - 1e v/d maand' },
  { value: '+M', positive: true, unit: 'M', label: 'maanden in de toekomst - 1e v/d maand' },
  { value: '-y', positive: false, unit: 'y', label: 'jaren in het verleden' },
  { value: '+y', positive: true, unit: 'y', label: 'jaren in de toekomst' },
  { value: '-Y', positive: false, unit: 'Y', label: 'jaren in het verleden - 1 januari' },
  { value: '+Y', positive: true, unit: 'Y', label: 'jaren in de toekomst - 1 januari' },
]

/**
 * Custom date input component
 * Only triggers search on blur (when calendar closes), never during onChange
 */
const DateInputComponent = ({ item, applyValue, focusElementRef }) => {
  const [localValue, setLocalValue] = React.useState(item.value || '')

  // Sync local value when item value changes externally
  React.useEffect(() => {
    setLocalValue(item.value || '')
  }, [item.value])

  const handleChange = (event) => {
    const inputValue = event.target.value
    // Only update local state - never trigger search
    setLocalValue(inputValue)
  }

  const handleBlur = (event) => {
    const inputValue = event.target.value
    // Only trigger search on blur (when user is done)
    if (inputValue && inputValue !== item.value) {
      applyValue({ ...item, value: inputValue })
    }
  }

  const handleKeyDown = (event) => {
    // Trigger search on Enter key for keyboard users
    if (event.key === 'Enter') {
      const inputValue = event.target.value
      if (inputValue && inputValue !== item.value) {
        applyValue({ ...item, value: inputValue })
      }
    }
  }

  return (
    <input
      ref={focusElementRef}
      type="date"
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      style={{
        width: '100%',
        padding: '8px',
        fontFamily: 'inherit',
        fontSize: '14px'
      }}
    />
  )
}

/**
 * Custom relative date input component
 * Allows users to select a number and a relative time period
 */
const RelativeInputComponent = ({ item, applyValue, focusElementRef }) => {
  const [localNumber, setLocalNumber] = React.useState('')
  const [localOption, setLocalOption] = React.useState(null)

  const parseValue = (value) => {
    if (!value || typeof value !== 'string') return { number: '', option: null }

    // Handle partial values
    if (value.startsWith('_PARTIAL_NUMBER_')) {
      const number = value.replace('_PARTIAL_NUMBER_', '')
      return { number, option: null }
    }

    if (value.startsWith('_PARTIAL_')) {
      const optionValue = value.replace('_PARTIAL_', '')
      const option = relativeOptions.find(opt => opt.value === optionValue)
      return { number: '', option }
    }

    // Parse existing combined value like "-2w" or "+1m"
    const match = value.match(/^([+-]?)(\d*)([wWmMyY]?)$/)
    if (match) {
      const [, sign, number, unit] = match
      const option = relativeOptions.find(opt => opt.value === `${sign}${unit}`)
      return { number: number || '', option }
    }

    return { number: '', option: null }
  }

  // Initialize local state from item value
  React.useEffect(() => {
    const { number, option } = parseValue(item.value)
    setLocalNumber(number)
    setLocalOption(option)
  }, [item.value])

  const triggerUpdate = (number, option) => {
    if (option && number) {
      const combinedValue = `${option.positive ? '+' : '-'}${number}${option.unit}`
      applyValue({ ...item, value: combinedValue })
    } else if (option && !number) {
      // Keep partial state - option selected but no number yet
      const partialValue = `_PARTIAL_${option.value}`
      applyValue({ ...item, value: partialValue })
    } else if (number && !option) {
      // Keep partial state - number entered but no option yet
      const partialValue = `_PARTIAL_NUMBER_${number}`
      applyValue({ ...item, value: partialValue })
    } else {
      applyValue({ ...item, value: '' })
    }
  }

  const handleNumberChange = (newNumber) => {
    setLocalNumber(newNumber)
    // Always trigger update with new values
    triggerUpdate(newNumber, localOption)
  }

  const handleOptionChange = (newOption) => {
    setLocalOption(newOption)
    // Always trigger update with new values
    triggerUpdate(localNumber, newOption)
  }

  return (
    <div style={{
      display: 'flex',
      gap: '8px',
      alignItems: 'stretch',
      width: '100%',
      maxWidth: '100%',
      overflow: 'visible'
    }}>
      <input
        ref={focusElementRef}
        type="number"
        value={localNumber}
        onChange={(e) => handleNumberChange(e.target.value)}
        style={{
          width: '100px',
          padding: '8px 12px',
          flexShrink: 0,
          fontSize: '1rem',
          fontFamily: 'ROsanswebtext, Calibri, sans-serif',
          border: '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: '4px',
          boxSizing: 'border-box',
          lineHeight: '1.5'
        }}
        min="1"
      />
      <select
        value={localOption?.value || ''}
        onChange={(e) => {
          if (e.target.value) {
            const selectedOption = relativeOptions.find(opt => opt.value === e.target.value)
            handleOptionChange(selectedOption)
          } else {
            handleOptionChange(null)
          }
        }}
        style={{
          width: 'calc(100% - 116px)',
          maxWidth: '850px',
          padding: '8px 12px',
          fontSize: '1rem',
          fontFamily: 'ROsanswebtext, Calibri, sans-serif',
          border: '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: '4px',
          boxSizing: 'border-box',
          lineHeight: '1.5',
          backgroundColor: '#fff',
          appearance: 'auto'
        }}
      >
        <option value="">Selecteer periode</option>
        {relativeOptions.map(opt => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  )
}

/**
 * Create custom relative date operators for MUI DataGrid
 *
 * @param {Object} intl - React Intl instance for translations
 * @returns {Array} Array of date operator configurations
 */
export const createRelativeDateOperators = (intl) => {
  // Base date operators with custom input component
  const baseDateOperators = getGridDateOperators().filter(
    (operator) => ['is', 'not', 'after', 'onOrAfter', 'before', 'onOrBefore'].includes(operator.value)
  ).map(operator => ({
    ...operator,
    InputComponent: DateInputComponent // Use custom input for all date operators
  }))

  // Relative date operators
  const relativeOperators = [
    {
      value: 'equalsRelative',
      label: intl.formatMessage({ id: 'label.operator_eq_relative' }),
      requiresFilterValue: true,
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null
        return () => true // Backend handles the logic
      },
      InputComponent: RelativeInputComponent,
    },
    {
      value: 'afterRelative',
      label: intl.formatMessage({ id: 'label.operator_gt_date_relative' }),
      requiresFilterValue: true,
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null
        return () => true // Backend handles the logic
      },
      InputComponent: RelativeInputComponent,
    },
    {
      value: 'beforeRelative',
      label: intl.formatMessage({ id: 'label.operator_lt_date_relative' }),
      requiresFilterValue: true,
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null
        return () => true // Backend handles the logic
      },
      InputComponent: RelativeInputComponent,
    }
  ]

  return [...baseDateOperators, ...relativeOperators]
}
